'use client';

import { useEffect, useState } from 'react';

import {
  IDeviceFeatureName,
  useDeviceAssessmentStore,
} from '@northladder/services';

import { useNavRouter } from './useNavRouter';

export type INavPage =
  | './'
  | '/assessment-instructions'
  | '/back-camera-init'
  | '/back-camera-evaluate'
  | '/back-camera-evaluate-success'
  | '/biometric-access'
  | '/biometric-authentication-success'
  | '/biometrics-init'
  | '/front-camera-init'
  | '/front-camera-access'
  | '/front-camera-evaluate'
  | '/front-camera-evaluate-success'
  | '/functional-assessment-complete'
  | '/motion-sensors-evaluation'
  | '/motion-sensors-evaluate'
  | '/motion-sensors-evaluation-success'
  | '/motion-sensors-init'
  | '/motion-sensor-prompt'
  | '/sensors-game-evaluate'
  | '/sound-performance-init'
  | '/sound-performance-mic-access'
  | '/sound-performance-evaluate'
  | '/sound-performance-evaluate-success'
  | '/touch-gestures-init'
  | '/touch-gestures-three-finger-evaluate'
  | '/touch-gestures-two-finger-evaluate'
  | '/touch-screen-evaluate-success'
  | '/touch-screen-evaluate'
  | '/touch-screen-init'
  | '/touch-screen-prompt';

interface IAssessmentStepsConfigItem {
  stepIndex: number;
  featureName: IDeviceFeatureName;
  path: INavPage;
  nextFeatureName: IDeviceFeatureName;
}

/**
 * Shows the steps of the assessment process and the corresponding feature name,
 * providing a path to jump to that step if it was missed.
 */
export const assessmentStepsConfig: Record<number, IAssessmentStepsConfigItem> =
  {
    1: {
      stepIndex: 1,
      featureName: 'Digitizer',
      nextFeatureName: 'Multitouch',
      path: '/touch-screen-init',
    },
    2: {
      stepIndex: 2,
      featureName: 'Multitouch',
      nextFeatureName: 'Speaker',
      path: '/touch-gestures-init',
    },
    3: {
      // Keep the microphone and speaker in the same step
      stepIndex: 3,
      featureName: 'Speaker',
      nextFeatureName: 'Biometrics',
      path: '/sound-performance-init',
    },
    4: {
      // Keep the microphone and speaker in the same step
      stepIndex: 4,
      featureName: 'Speaker',
      nextFeatureName: 'Biometrics',
      path: '/sound-performance-init',
    },
    5: {
      stepIndex: 5,
      featureName: 'Biometrics',
      nextFeatureName: 'Front Camera',
      path: '/biometrics-init',
    },
    6: {
      stepIndex: 6,
      featureName: 'Front Camera',
      nextFeatureName: 'Back Camera',
      path: '/front-camera-init',
    },
    7: {
      stepIndex: 7,
      featureName: 'Back Camera',
      nextFeatureName: 'Gyroscope',
      path: '/back-camera-init',
    },
    8: {
      // Keep the motion sensors in the same step
      stepIndex: 8,
      featureName: 'Gyroscope',
      nextFeatureName: 'Accelerometer',
      path: '/motion-sensors-init',
    },
    9: {
      // Keep the motion sensors in the same step
      stepIndex: 9,
      featureName: 'Accelerometer',
      nextFeatureName: 'Accelerometer',
      path: '/motion-sensors-init',
    },
  };

interface IOtherFeatureOptions {
  /**
   * Another feature to be marked as faulty in case the targe feature is not
   * working. Such as if the microphone is not working, disabling the speaker too.
   * TODO NOTE: This is a flaw for now but will be fixed in the future.
   */
  otherFeature?: IDeviceFeatureName;
}

interface IContinueOptions {
  /**
   * Another feature to be marked as valid in case the targe feature is valid too
   * Such as if the Accelerometer is working, Gyroscope should be working too
   * since the game uses both features
   */
  otherFeature?: IDeviceFeatureName;
}

export interface IUseDeviceAssessmentInitializationOptions {
  featureName: IDeviceFeatureName;
  nextFeatureName?: IDeviceFeatureName;
  nextFeatureScreenPath: INavPage;
  nextScreenPath: INavPage;
  prevScreenPath?: INavPage;
  stepIndex: number;
}

/**
 * This sets up the base routing logic and validation for a new feature.
 * Used mostly in the initialization of the device assessment process.
 *
 * - Mostly to be used in pages with `-init` postfix.
 */
export const useDeviceAssessmentInitialization = ({
  featureName,
  nextFeatureName,
  stepIndex,
  nextFeatureScreenPath,
  nextScreenPath,
  prevScreenPath = './',
}: IUseDeviceAssessmentInitializationOptions) => {
  /**
   * The final screen path to navigate to when all the features have been assessed.
   */
  const FINAL_SCREEN_PATH = '/functional-assessment-complete';

  const { evaluationId, navigateToRoute, prefetchRoute, resetNavPath } =
    useNavRouter();
  const [isFeatureAlreadyAssessed, setIsFeatureAssessed] =
    useState<boolean>(false);

  const {
    assessments,
    onUpdateFeatureAssessment,
    evaluationId: storedEvaluationId,
  } = useDeviceAssessmentStore();

  useEffect(() => {
    if (assessments) {
      const assessedComponents = new Set(
        assessments.map(({ component }) => component)
      );

      if (nextFeatureName !== undefined) {
        const isFeatureAssessmentDone = assessedComponents.has(nextFeatureName);

        setIsFeatureAssessed(isFeatureAssessmentDone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessments, nextFeatureName]);

  useEffect(() => {
    if (nextScreenPath) {
      prefetchRoute(nextScreenPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnContinueToNextPage = (options?: IContinueOptions) => {
    const { otherFeature } = options || {};

    onUpdateFeatureAssessment({
      component: featureName,
      condition: 'Working',
      stepIndex,
    });

    if (otherFeature) {
      onUpdateFeatureAssessment({
        component: otherFeature,
        condition: 'Working',
        stepIndex,
      });
    }

    if (isFeatureAlreadyAssessed) {
      navigateToRoute(FINAL_SCREEN_PATH);
    } else {
      navigateToRoute(nextScreenPath);
    }
  };

  const handleOnJustNavToNextPage = () => {
    navigateToRoute(nextScreenPath);
  };

  /**
   * This will allow the user to skip over this feature and continue to the
   * the next feature evaluation with out marking this as faulty.
   * TODO: Mark this as `N/A` to the final backend payload.
   */
  const handleOnSkipToNextPage = () => {
    navigateToRoute(nextScreenPath);
  };

  const handleNavToBackToPrompt = () => {
    navigateToRoute(prevScreenPath);
  };

  const handleOnNotWorkingModalCancelAction = () => {
    // NOTE: Modal gets closed, do nothing for now.
  };

  const handleOnNotWorkingModalContinueAction = (
    options?: IOtherFeatureOptions
  ) => {
    const { otherFeature } = options || {};

    onUpdateFeatureAssessment({
      component: featureName,
      condition: 'Faulty',
      stepIndex,
    });

    if (otherFeature) {
      onUpdateFeatureAssessment({
        component: otherFeature,
        condition: 'Faulty',
        stepIndex,
      });
    }
    if (isFeatureAlreadyAssessed) {
      navigateToRoute(FINAL_SCREEN_PATH);
    } else {
      navigateToRoute(nextFeatureScreenPath);
    }
  };

  /**
   * This will only be called in cases the component has not got a modal, so the
   * action is triggered directly from the text button click.
   * TODO: Revise this action to see if it can be removed.
   */
  const handleOnOtherAction = () => {
    navigateToRoute(nextFeatureScreenPath);
  };

  const featuresToBeEvaluated: IDeviceFeatureName[] = [
    'Digitizer',
    'Multitouch',
    'Microphone',
    'Speaker',
    'Biometrics',
    'Front Camera',
    'Back Camera',
  ];

  /**
   * Navigates to the page corresponding to the missing device feature component.
   *
   * @param featureComponent The missing device feature component.
   */
  const handleOnNavToMissingFeature = (
    featureComponent: IDeviceFeatureName
  ) => {
    let targetPage: INavPage;
    switch (featureComponent) {
      case 'Digitizer': {
        targetPage = '/touch-screen-init';
        break;
      }
      case 'Multitouch': {
        targetPage = '/touch-gestures-init';
        break;
      }
      case 'Speaker': {
        targetPage = '/sound-performance-init';
        break;
      }
      case 'Microphone': {
        targetPage = '/sound-performance-init';
        break;
      }
      case 'Biometrics': {
        targetPage = '/biometrics-init';
        break;
      }
      case 'Front Camera': {
        targetPage = '/front-camera-init';
        break;
      }
      case 'Back Camera': {
        targetPage = '/back-camera-init';
        break;
      }
      default: {
        targetPage = '/assessment-instructions';
      }
    }
    setTimeout(() => {
      navigateToRoute(targetPage);
    }, 2000);
  };

  return {
    /**
     * The current status of the device features assessments.
     */
    assessments,
    /**
     * The evaluation ID of the current assessment session.
     */
    evaluationId: storedEvaluationId ?? evaluationId,
    /**
     * The list of feature to be eval??ted.
     */
    featuresToBeEvaluated,
    notWorkingModalCallbacks: {
      /**
       * Called when user cancels the modal for the functionality not working.
       * Allows them to reread what they missed so not implication to the journey.
       */
      onCancelAction: handleOnNotWorkingModalCancelAction,
      /**
       * Called when user confirm that the functionality is not working and they
       * continues assessment anyways. Results in the feature being marked as faulty.
       */
      onContinueAction: handleOnNotWorkingModalContinueAction,
    },
    /**
     * Navigates the user to the next page whilst also evaluation current page
     * with the results from the user interaction.
     */
    onContinueToNextPage: handleOnContinueToNextPage,
    /**
     * Navigates the user to the next page to begin the evaluation process,
     * This is usually fired when the current page is just a prompt and the
     * has no validation to be done.
     */
    onJustNavToNextPage: handleOnJustNavToNextPage,
    /**
     * This will allow the user to skip over this feature and continue to the
     * the next feature evaluation with out marking this as faulty.
     * TODO: Mark this as `N/A` to the final backend payload.
     */
    onSkipToNextPage: handleOnSkipToNextPage,
    /**
     * Navigates the user to the previous page to begin the restart the process.
     */
    onNavToBackToPrompt: handleNavToBackToPrompt,
    /**
     * This will only be called in cases the component has not got a modal, so the
     * action is triggered directly from the text-button click.
     */
    onOtherAction: handleOnOtherAction,
    /**
     * This will only be called in case any feature component test is missing.
     */
    onNavToMissingFeature: handleOnNavToMissingFeature,
    /**
     * Navigates the user back to the start screen prompting them to restart
     * the entire assessment process.
     */
    onRestartWholeAssessment: resetNavPath,
  };
};
